import React, { FC } from 'react';

import { Anchor, Flex, Loader, LoadingOverlay, Stack, Text } from '@mantine/core';

import { Component } from 'models';

import {
    DiagramComponentOverviewHit,
    DraggableDiagramComponentOverviewHit,
} from 'components/component-overview/ComponentOverviewHit.Diagram';
import { ImpressionTracker } from 'components/component-overview/components/ImpressionTracker';
import { EmptyMessage } from 'components/empty-message/EmptyMessage';
import { GridSection } from 'components/section/GridSection';
import { getPromoIndex } from 'components/section/getPromoIndex';
import { useResponsiveNbCols } from 'components/section/useResponsiveNbCols';
import { useDistributorPricesByComponentIds } from 'hooks/use-distributor-prices-by-component-ids';
import { useLocalEvent } from 'hooks/use-local-event';

import { ComponentOverviewHit } from './ComponentOverviewHit';

type ComponentHit = Component & { isFeatured?: boolean; __position?: number };

const ComponentOverviewHits: FC<{
    hits: ComponentHit[];
    isLoading?: boolean;
    emptyMessage?: React.ReactNode;
    promoCard?: React.ReactNode;
    showCompatibleWith?: boolean;
    nbCols?: number;
    renderActions?: (component: Component) => React.ReactNode;
}> = ({ hits, emptyMessage, isLoading, promoCard, showCompatibleWith, nbCols = 4, renderActions }) => {
    const { localEvent, setLocalEvent } = useLocalEvent();
    const responsiveNbCols = useResponsiveNbCols(nbCols);
    const { priceFor } = useDistributorPricesByComponentIds(hits.map((hit) => hit.id));

    const promoIndex = promoCard && responsiveNbCols ? getPromoIndex(responsiveNbCols, hits.length) : -1;

    return hits.length ? (
        <GridSection nbCols={nbCols}>
            {/* let's keep showing old results to prevent big page jumps */}
            <LoadingOverlay
                visible={isLoading}
                zIndex={200}
                loaderProps={{ size: 24, mt: 80, color: 'dimmed', style: { alignSelf: 'start' } }}
            />
            {hits.map((component, index) => (
                <React.Fragment key={component.id}>
                    {index === promoIndex && promoCard}
                    <ImpressionTracker
                        namespace="component.search"
                        data={{
                            component: component.id,
                            company: component.manufacturer,
                        }}
                        position={component.__position}
                    >
                        <ComponentOverviewHit
                            showBooth
                            component={component}
                            distributorPrice={priceFor(component.id)}
                            showEmptyImage
                            showCompatibleWith={showCompatibleWith}
                            featured={component.isFeatured}
                        >
                            {renderActions?.(component)}
                        </ComponentOverviewHit>
                    </ImpressionTracker>
                </React.Fragment>
            ))}
            {promoIndex === hits.length && promoCard}
        </GridSection>
    ) : isLoading ? (
        <Flex justify="center" mt="xl">
            <Loader size="sm" />
        </Flex>
    ) : (
        <EmptyMessage>
            {emptyMessage || <>Nothing found, try different search criteria?</>}

            {localEvent && (
                <Flex justify="center">
                    <Anchor inherit fw={600} onClick={() => setLocalEvent(null)}>
                        Try again without the {localEvent.name} filter
                    </Anchor>
                </Flex>
            )}
        </EmptyMessage>
    );
};

const DiagramComponentOverviewHits: FC<{
    hits: Component[];
    isLoading?: boolean;
    draggable?: boolean;
}> = ({ hits, isLoading, draggable }) => {
    const { priceFor } = useDistributorPricesByComponentIds(hits.map((hit) => hit.id));

    if (isLoading)
        return (
            <Flex justify="center" mt="xl">
                <Loader size="sm" />
            </Flex>
        );

    return !hits.length ? (
        <Text c={'dimmed'} mt="xs" ta="center" fz="sm">
            No products found. Try adjusting the filters.
        </Text>
    ) : (
        <Stack gap={8} pos="relative">
            {hits.map((component) =>
                draggable ? (
                    <DraggableDiagramComponentOverviewHit
                        key={component.id}
                        component={component}
                        distributorPrice={priceFor(component.id)}
                    />
                ) : (
                    <DiagramComponentOverviewHit
                        key={component.id}
                        component={component}
                        distributorPrice={priceFor(component.id)}
                    />
                ),
            )}
        </Stack>
    );
};

export { ComponentOverviewHits, DiagramComponentOverviewHits };
